import {
  differenceInDays,
  differenceInMonths,
  hoursToMinutes,
  isBefore,
  minutesToSeconds,
  parse,
  formatISO,
  format,
} from 'date-fns'
import { formatInTimeZone, toDate } from 'date-fns-tz'

import { DATE_REGEX, MONTH_YEAR_DATE_REGEX, REMOVE_TIME_REGEX } from './regex'

export const NY_TIMEZONE = 'America/New_York'

export const formatDate = (date: Date | string | undefined | null, dateDisplayFormat: string) => {
  if (!date) {
    return ''
  }

  const newDate = typeof date === 'string' ? new Date(date) : date
  return formatInTimeZone(newDate, NY_TIMEZONE, dateDisplayFormat)
}

export const getNYUTCOffset = () => `UTC${formatDate(new Date(), 'XXX')}`

export const toISODate = (date: Date) => {
  if (!date) {
    return ''
  }

  return formatISO(date)
}

export const dateRange = (startAt: number, endAt: number): ReadonlyArray<number> => {
  const size = endAt - startAt

  return [...Array(size).keys()].map(i => i + startAt)
}

// DD/MM/YYYY to MM-DD-YYYY
export const formatToUS = (date: string) => {
  const [day, month, year] = date.split('/')
  return `${month}-${day}-${year}`
}

// DD/MM/YYYY to Date
export const dayMonthYearToDate = (str: string) => {
  const yearMonthDay: string[] = str.split('/')

  return new Date(Number(yearMonthDay[2]), Number(yearMonthDay[1]) - 1, Number(yearMonthDay[0]))
}

// MM/YYYY to Date
export const monthYearToDate = (str: string) => {
  const yearMonthDay: string[] = str.split('/')

  return new Date(Number(yearMonthDay[1]), Number(yearMonthDay[0]) - 1, 1)
}

// YYYY-MM-DD to MM/YYYY
export const ISOToMonthYear = (value: string | null | undefined) => {
  if (!value) {
    return ''
  }

  return formatDate(value, 'MM/yyyy')
}

// MM/YYYY to YYYY-MM-DD
export const monthYearToISO = (value: string | null | undefined) => {
  if (!value) {
    return ''
  }

  const monthYear = value.split('/')
  const year = monthYear[1]
  const month = monthYear[0]

  return `${year}-${month}-01`
}

export const dateFormat = (date: string) => {
  const splitDate = date.split('/')
  const [month, day, year] = splitDate

  return `${year}-${month}-${day}`
}

export const hoursAndMinutesToSeconds = (value: string) => {
  const [hours, minutes] = value.toLowerCase().split('h')
  const convertHoursToMinutes = hoursToMinutes(parseInt(hours?.replace('h', '')))
  const totalOfMinutes = convertHoursToMinutes + parseInt(minutes?.replace('m', ''))
  const convertMinutesToSeconds = minutesToSeconds(totalOfMinutes)
  return convertMinutesToSeconds
}

export const timeLeftFromNow = (date: Date, float?: boolean) => {
  if (!date) {
    return ''
  }

  const today = new Date()
  const comparedDate = new Date(date)
  const daysDifference = differenceInDays(comparedDate, today)

  if (daysDifference > 31) {
    const monthsDifference = float ? daysDifference / 30 : differenceInMonths(comparedDate, today)
    return `${monthsDifference} month${monthsDifference > 1 ? 's' : ''} left`
  }

  if (daysDifference >= 1) {
    return daysDifference > 1 ? `${daysDifference} days` : 'Today'
  }

  return 'Ended'
}

export const getDifferenceInMonths = (firstDate: string, secondDate: string) => {
  if (!firstDate || !secondDate) {
    return ''
  }

  const date = new Date(firstDate)
  const comparedDate = new Date(secondDate)

  const numberOfMonths = differenceInMonths(comparedDate, date)
  const numberOfDays = differenceInDays(comparedDate, date)
  const monthsLabel = numberOfMonths === 1 ? `${numberOfMonths} month` : `${numberOfMonths} months`
  const daysLabel = numberOfDays === 1 ? `${numberOfDays} day` : `${numberOfDays} days`

  return numberOfMonths > 0 ? monthsLabel : daysLabel
}

export const isValidDate = (date: string) => {
  return MONTH_YEAR_DATE_REGEX.test(date) || DATE_REGEX.test(date)
}

export const stringToDate = (date: string, formatString: string) =>
  parse(date, formatString, new Date())

// TODO: Improve stringToNewYorkISO function
// Get data from dd/MM/yyyy to America/New York timezone
export const stringToNewYorkISO = (date: string, isEnd = false) => {
  const [day, month, year] = date.split('/')
  const string = `${year}-${month}-${day}`
  const newTime = isEnd ? '23:59:59' : '00:00:00'

  const newDate = toDate(`${string}T${newTime}`, { timeZone: NY_TIMEZONE })

  return newDate
}

export const ISODateToNowTime = (date: Date) => {
  const ISODate = toISODate(date)
  const now = toISODate(new Date())

  const dateWithoutTime = ISODate.split('T')[0]

  const nowTime = now.split('T')[1]

  const newIsoDate = `${dateWithoutTime}T${nowTime}`

  return newIsoDate
}

export const getDifferenceInDays = (date: string, dateToCompare?: string) => {
  if (!date) {
    return ''
  }

  return differenceInDays(new Date(date), dateToCompare ? new Date(dateToCompare) : new Date())
}

export const isTodayOrLater = (date: string) => {
  if (!date) {
    return false
  }

  const today = new Date()
  const comparedDate = new Date(date)

  return !isBefore(today, comparedDate)
}

// ISO string
export const removeDateTimezone = (date: string) => {
  if (date.includes('+')) {
    return date.split('+')?.[0]
  }

  if (date.includes('Z')) {
    return date.split('Z')?.[0]
  }

  const arrayDate = date.split('-')
  arrayDate.pop()
  return arrayDate.join('-')
}
// ISO string
export const formatWithoutTimezone = (date: string, dateDisplayFormat: string) => {
  if (!date) {
    return ''
  }
  const newDate = new Date(removeDateTimezone(date))

  return format(newDate, dateDisplayFormat)
}

const removeTimeFromDate = (date: Date | string) => {
  if (typeof date === 'string') {
    return date.replace(REMOVE_TIME_REGEX, '')
  }

  const ISODate = toISODate(date)
  return ISODate.replace(REMOVE_TIME_REGEX, '')
}

export const differenceInDaysToToday = (date: Date | string) => {
  if (!date) {
    return ''
  }

  const newDate = removeTimeFromDate(date)

  const days = differenceInDays(new Date(), new Date(newDate))

  if (days === 0) {
    return 'Today'
  }

  if (days < 2) {
    return 'Yesterday'
  }

  if (days > 31) {
    return formatDate(date, 'MMM do, yyyy')
  }

  return `${days}d ago`
}

// TODO: Check the necessity of this formatter when Company payments integrated
// "new Date()" replacer to avoid timezone problems
export const dateWithoutTimezone = (date?: string) => {
  // reduces 1 days because of GMT -3 timezone
  const newDate = date ? new Date(date) : new Date()

  // Converts to ISO string and remove "Z" and call date Object with new string
  const dateObjectWithoutTimeZone = new Date(newDate.toISOString().slice(0, -1))
  return dateObjectWithoutTimeZone
}

export const formatReadableDate = (date: string) => {
  const formattedDate = differenceInDaysToToday(date)

  if (formattedDate === 'Today' || formattedDate === 'Yesterday' || formattedDate.includes('ago')) {
    return formattedDate
  }

  return `on ${formattedDate}`
}
